import { EquipmentCalculatorSectionKey } from "@/composables/scenarioCalculations/calculationTypes";
import { Severity, valuesExist } from "../scenarioValidator";
import type { CorrelationValidationGenerator } from "./correlationChecks";

export const correlationsAtSensorChecks: CorrelationValidationGenerator = (
  correlationLimits,
  correlationField,
  { calcs }
) => {
  const sensorTemperatureF = calcs.downholeSensorTemperatureF.value;
  const sensorPressurePSIA = calcs.wellFlowingPressureAtSensorPSIA.value;
  const sensorFluidProperties =
    calcs.fluidProperties.fluidPropertiesAtSensor.value;

  return [
    {
      name: `${correlationField}_oil_gravity_sensor`,
      message: `API Out of Range @ Sensor`,
      severity: Severity.warn,
      rulePlacement: EquipmentCalculatorSectionKey.SEAL,
      isActive: valuesExist([
        correlationLimits.MIN_API,
        correlationLimits.MAX_API,
        calcs.basicData.value.oilAPI
      ]),
      isInvalid:
        calcs.basicData.value.oilAPI > correlationLimits.MAX_API ||
        calcs.basicData.value.oilAPI < correlationLimits.MIN_API
    },
    {
      name: `${correlationField}_gas_specific_gravity_sensor`,
      message: `Gas Specific Gravity Out of Range @ Sensor`,
      severity: Severity.warn,
      rulePlacement: EquipmentCalculatorSectionKey.SEAL,
      isActive: valuesExist([
        correlationLimits.MIN_GAS_SG,
        correlationLimits.MAX_GAS_SG,
        calcs.basicData.value.gasSpecificGravity
      ]),
      isInvalid:
        calcs.basicData.value.gasSpecificGravity >
          correlationLimits.MAX_GAS_SG ||
        calcs.basicData.value.gasSpecificGravity < correlationLimits.MIN_GAS_SG
    },
    // Double check this one
    {
      name: `${correlationField}_temperature_sensor`,
      message: `Temperature Out of Range @ Sensor`,
      severity: Severity.warn,
      rulePlacement: EquipmentCalculatorSectionKey.SEAL,
      isActive:
        sensorTemperatureF !== undefined &&
        valuesExist([
          correlationLimits.MIN_TEMP,
          correlationLimits.MAX_TEMP,
          sensorTemperatureF
        ]),
      isInvalid:
        sensorTemperatureF !== undefined &&
        (sensorTemperatureF > correlationLimits.MAX_TEMP ||
          sensorTemperatureF < correlationLimits.MIN_TEMP)
    },
    {
      name: `${correlationField}_pressure_sensor`,
      message: `Pressure Out of Range @ Sensor`,
      severity: Severity.warn,
      rulePlacement: EquipmentCalculatorSectionKey.SEAL,
      isActive:
        sensorPressurePSIA !== undefined &&
        valuesExist([
          correlationLimits.MIN_PRESS,
          correlationLimits.MAX_PRESS,
          sensorPressurePSIA
        ]),
      isInvalid:
        sensorPressurePSIA !== undefined &&
        (sensorPressurePSIA > correlationLimits.MAX_PRESS ||
          sensorPressurePSIA < correlationLimits.MIN_PRESS)
    },
    {
      name: `${correlationField}_oil_solution_gas_sensor`,
      message: `Oil Solution Gas Out of Range @ Sensor`,
      severity: Severity.warn,
      rulePlacement: EquipmentCalculatorSectionKey.SEAL,
      isActive:
        sensorFluidProperties !== undefined &&
        valuesExist([
          correlationLimits.MIN_RSO,
          correlationLimits.MAX_RSO,
          sensorFluidProperties.oilPhaseGasSolubilitySCFSTB
        ]),
      isInvalid:
        sensorFluidProperties !== undefined &&
        (sensorFluidProperties.oilPhaseGasSolubilitySCFSTB >
          correlationLimits.MAX_RSO ||
          sensorFluidProperties.oilPhaseGasSolubilitySCFSTB <
            correlationLimits.MIN_RSO)
    },
    {
      name: `${correlationField}_bubblepoint_pressure_sensor`,
      message: `Bubblepoint Pressure Out of Range @ Sensor`,
      severity: Severity.warn,
      rulePlacement: EquipmentCalculatorSectionKey.SEAL,
      isActive:
        sensorFluidProperties !== undefined &&
        valuesExist([
          correlationLimits.MIN_PB,
          correlationLimits.MAX_PB,
          sensorFluidProperties.bubblePointPressurePSIA
        ]),
      isInvalid:
        sensorFluidProperties !== undefined &&
        (sensorFluidProperties.bubblePointPressurePSIA >
          correlationLimits.MAX_PB ||
          sensorFluidProperties.bubblePointPressurePSIA <
            correlationLimits.MIN_PB)
    },
    {
      name: `${correlationField}_oil_formation_volume_factor_sensor`,
      message: `Oil Formation Volume Factor Out of Range @ Sensor`,
      severity: Severity.warn,
      rulePlacement: EquipmentCalculatorSectionKey.SEAL,
      isActive:
        sensorFluidProperties !== undefined &&
        valuesExist([
          correlationLimits.MIN_BO,
          correlationLimits.MAX_BO,
          sensorFluidProperties.oilVolumeFactorBBLSTB
        ]),
      isInvalid:
        sensorFluidProperties !== undefined &&
        (sensorFluidProperties.oilVolumeFactorBBLSTB >
          correlationLimits.MAX_BO ||
          sensorFluidProperties.oilVolumeFactorBBLSTB <
            correlationLimits.MIN_BO)
    },
    {
      name: `${correlationField}_dead_oil_viscosity_sensor`,
      message: `Dead Oil Viscosity Out of Range @ Sensor`,
      severity: Severity.warn,
      rulePlacement: EquipmentCalculatorSectionKey.SEAL,
      isActive:
        sensorFluidProperties !== undefined &&
        valuesExist([
          correlationLimits.MIN_U_OD,
          correlationLimits.MAX_U_OD,
          sensorFluidProperties.deadOilViscosityCP
        ]),
      isInvalid:
        sensorFluidProperties !== undefined &&
        (sensorFluidProperties.deadOilViscosityCP >
          correlationLimits.MAX_U_OD ||
          sensorFluidProperties.deadOilViscosityCP < correlationLimits.MIN_U_OD)
    },
    {
      name: `${correlationField}_live_oil_viscosity_sensor`,
      message: `Live Oil Viscosity Out of Range @ Sensor`,
      severity: Severity.warn,
      rulePlacement: EquipmentCalculatorSectionKey.SEAL,
      isActive:
        sensorFluidProperties !== undefined &&
        valuesExist([
          correlationLimits.MIN_U_O,
          correlationLimits.MAX_U_O,
          sensorFluidProperties.oilViscosityCP
        ]),
      isInvalid:
        sensorFluidProperties !== undefined &&
        (sensorFluidProperties.oilViscosityCP > correlationLimits.MAX_U_O ||
          sensorFluidProperties.oilViscosityCP < correlationLimits.MIN_U_O)
    },
    {
      name: `${correlationField}_pseudo_reduced_pressure_sensor`,
      message: `Pseudo-Reduced Pressure Out of Range @ Sensor`,
      severity: Severity.warn,
      rulePlacement: EquipmentCalculatorSectionKey.SEAL,
      isActive:
        sensorFluidProperties !== undefined &&
        valuesExist([
          correlationLimits.MIN_PPR,
          correlationLimits.MAX_PPR,
          sensorFluidProperties.psuedoCriticals.psuedoReducedPressure
        ]),
      isInvalid:
        sensorFluidProperties !== undefined &&
        (sensorFluidProperties.psuedoCriticals.psuedoReducedPressure >
          correlationLimits.MAX_PPR ||
          sensorFluidProperties.psuedoCriticals.psuedoReducedPressure <
            correlationLimits.MIN_PPR)
    },
    {
      name: `${correlationField}_pseudo_reduced_temperature_sensor`,
      message: `Pseudo-Reduced Temperature Out of Range @ Sensor`,
      severity: Severity.warn,
      rulePlacement: EquipmentCalculatorSectionKey.SEAL,
      isActive:
        sensorFluidProperties !== undefined &&
        valuesExist([
          correlationLimits.MIN_TPR,
          correlationLimits.MAX_TPR,
          sensorFluidProperties.psuedoCriticals.psuedoReducedTemperature
        ]),
      isInvalid:
        sensorFluidProperties !== undefined &&
        (sensorFluidProperties.psuedoCriticals.psuedoReducedTemperature >
          correlationLimits.MAX_TPR ||
          sensorFluidProperties.psuedoCriticals.psuedoReducedTemperature <
            correlationLimits.MIN_TPR)
    }
  ];
};
